import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Spinner, Pill } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { LOGO_URL } from "../lib/theme";
import { Users, Calendar, FileText, Zap, UserPlus, CalendarPlus, FilePlus } from "lucide-react";
import { formatDate } from "../lib/dates";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/summary").then(setData).finally(() => setLoading(false));
  }, []);

  const headerRight = null;

  const titleBlock = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <img src={LOGO_URL} alt="" style={{ width: 28, height: 28, borderRadius: 7 }} />
      <div>
        <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.2 }}>Studio Grape</div>
        <div style={{ fontSize: 10, color: "var(--muted)", letterSpacing: 0.4, fontWeight: 700, textTransform: "uppercase" }}>{user?.role}</div>
      </div>
    </div>
  );

  if (loading) return <><AppHeader title={titleBlock} right={headerRight} /><div className="sg-main"><Spinner /></div></>;
  if (!data) return <><AppHeader title={titleBlock} right={headerRight} /><div className="sg-main">Could not load dashboard.</div></>;

  const kpis = [
    { label: "Customers", value: data.customers_total, hint: `${data.converted} converted`, icon: Users, color: "var(--brand)", route: "/customers" },
    { label: "Today", value: data.meetings_today, hint: `${data.meetings_upcoming} upcoming`, icon: Calendar, color: "var(--success)", route: "/meetings" },
    { label: "Open Quotes", value: data.quotes_open, hint: `${data.quotes_approved} approved`, icon: FileText, color: "var(--warning)", route: "/quotes" },
    { label: "Follow-ups", value: data.followups_open, hint: "due soon", icon: Zap, color: "var(--danger)", route: "/more" },
  ];

  return (
    <>
      <AppHeader title={titleBlock} right={headerRight} />
      <div className="sg-main sg-fade-in">
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: "var(--muted)" }}>Good day,</div>
          <h1 className="sg-page-title">{user?.name?.split(" ")[0] || "there"}</h1>
        </div>

        <div className="sg-kpi-grid" style={{ marginBottom: 18 }}>
          {kpis.map((k) => {
            const Icon = k.icon;
            return (
              <button
                key={k.label}
                onClick={() => navigate(k.route)}
                className="sg-card sg-fade-in"
                data-testid={`dashboard-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
                style={{ textAlign: "left", cursor: "pointer", padding: 14 }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--surface-subtle)", display: "grid", placeItems: "center", marginBottom: 8, color: k.color }}>
                  <Icon size={16} />
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: "var(--ink)", letterSpacing: -0.5, lineHeight: 1 }}>{k.value}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)", marginTop: 6 }}>{k.label}</div>
                <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{k.hint}</div>
              </button>
            );
          })}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 18 }}>
          <button className="sg-btn" onClick={() => navigate("/customers/new")} data-testid="quick-new-customer" style={{ flexDirection: "column", padding: "12px 4px", gap: 4 }}>
            <UserPlus size={18} color="var(--brand)" />
            <span style={{ fontSize: 11, fontWeight: 700 }}>New Customer</span>
          </button>
          <button className="sg-btn" onClick={() => navigate("/meetings/new")} data-testid="quick-new-meeting" style={{ flexDirection: "column", padding: "12px 4px", gap: 4 }}>
            <CalendarPlus size={18} color="var(--brand)" />
            <span style={{ fontSize: 11, fontWeight: 700 }}>Book Meeting</span>
          </button>
          <button className="sg-btn" onClick={() => navigate("/quotes/new")} data-testid="quick-new-quote" style={{ flexDirection: "column", padding: "12px 4px", gap: 4 }}>
            <FilePlus size={18} color="var(--brand)" />
            <span style={{ fontSize: 11, fontWeight: 700 }}>New Quote</span>
          </button>
        </div>

        <div className="sg-section-h" style={{ marginTop: 16 }}>Recent Meetings</div>
        {(data.recent_meetings || []).length === 0 ? (
          <div className="sg-card" style={{ color: "var(--muted)", fontSize: 13 }}>No meetings yet.</div>
        ) : (
          (data.recent_meetings || []).slice(0, 3).map((m) => (
            <div key={m.meeting_id} onClick={() => navigate(`/meetings/${m.meeting_id}`)} className="sg-list-card">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                <span style={{ fontWeight: 700, fontSize: 14, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.customer_name}</span>
                <Pill label={m.status} tone={m.status === "completed" ? "success" : m.status === "cancelled" ? "danger" : "brand"} />
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>{formatDate(m.date)} · {m.time} · {String(m.meeting_type).replace("_", " ")}</div>
            </div>
          ))
        )}

        <div className="sg-section-h">Recent Quotes</div>
        {(data.recent_quotes || []).length === 0 ? (
          <div className="sg-card" style={{ color: "var(--muted)", fontSize: 13 }}>No quotes yet.</div>
        ) : (
          (data.recent_quotes || []).slice(0, 4).map((q) => (
            <div key={q.quote_id} onClick={() => navigate(`/quotes/${q.quote_id}`)} className="sg-list-card">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                <span style={{ fontWeight: 800, color: "var(--brand)", letterSpacing: 0.4, fontSize: 13 }}>{q.quote_number}</span>
                <Pill label={q.status} tone={q.status === "paid" || q.status === "approved" ? "success" : q.status === "rejected" ? "danger" : "neutral"} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{q.customer_name}</div>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>
                {String(q.quote_type).replace("_", " ")} · ₹{Number(q.amount).toLocaleString("en-IN")}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
